import React, { useState } from 'react';
import { motion } from 'framer-motion';
import './App.css';
import Header from './components/Header';
import Countdown from './components/Countdown';
import EventInfo from './components/EventInfo';
import PhotoGallery from './components/PhotoGallery';
import OfficialPoster from './components/OfficialPoster';

function App() {
  const [language, setLanguage] = useState('fr');
  
  // Date du concert: 16 mai 2026 à 19h
  const concertDate = new Date('2026-05-16T19:00:00');

  const content = {
    fr: {
      hero: 'Charlotte Dipanda',
      heroSub: 'EN CONCERT',
      subtitle: 'Une soirée exceptionnelle vous attend',
      cta: 'RÉSERVER VOS BILLETS',
      date: '16 MAI 2026',
      venue: 'CIRQUE ROYAL • BRUXELLES',
      footer: '© 2025 Charlotte Dipanda Concert. Tous droits réservés.'
    },
    en: {
      hero: 'Charlotte Dipanda',
      heroSub: 'IN CONCERT',
      subtitle: 'An exceptional evening awaits you',
      cta: 'GET YOUR TICKETS',
      date: 'MAY 16, 2026',
      venue: 'CIRQUE ROYAL • BRUSSELS',
      footer: '© 2025 Charlotte Dipanda Concert. All rights reserved.'
    }
  };

  const text = content[language];

  return (
    <div className="min-h-screen bg-navy relative overflow-hidden">
      {/* Animated Background - Optimisé */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-blue-900 to-navy"></div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/15 rounded-full blur-2xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/15 rounded-full blur-2xl"></div>
      </div>

      <div className="relative z-10">
        <Header language={language} onLanguageChange={setLanguage} />
        
        {/* Hero Section - SPECTACULAIRE */}
        <section className="min-h-screen flex items-center justify-center px-4 pt-20 pb-16 relative overflow-hidden">
          {/* Image du Cirque Royal en arrière-plan */}
          <div className="absolute inset-0 z-0">
            <img 
              src="/images/cirque-royal.jpg" 
              alt="Cirque Royal" 
              className="w-full h-full object-cover"
            />
            {/* Voile sombre pour garder le texte visible */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-navy/90"></div>
          </div>
          
          {/* Spotlight effect - Optimisé */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full pointer-events-none z-[1]">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-gradient-radial from-white/8 to-transparent rounded-full blur-2xl"></div>
          </div>

          <div className="container mx-auto text-center relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
            >
              <motion.h1 
                className="text-6xl md:text-8xl lg:text-9xl font-black text-white mb-2 tracking-tight drop-shadow-2xl"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.2, ease: "easeOut" }}
              >
                {text.hero}
              </motion.h1>
              <motion.div
                className="text-3xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent mb-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 1 }}
              >
                {text.heroSub}
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 1 }}
              className="space-y-4 mb-12"
            >
              <p className="text-xl md:text-3xl text-white font-bold tracking-wider drop-shadow-lg">
                {text.date}
              </p>
              <p className="text-lg md:text-xl text-white/90 tracking-widest drop-shadow-lg">
                {text.venue}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.8 }}
            >
              <a
                href="https://www.ticketmaster.be/event/289249177"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block group relative"
              >
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full blur opacity-75 group-hover:opacity-100 transition duration-300"></div>
                <div className="relative px-12 py-6 bg-navy rounded-full text-white text-xl md:text-2xl font-bold hover:bg-navy-dark transition-all duration-300 transform group-hover:scale-105 will-change-transform">
                  🎫 {text.cta}
                </div>
              </a>
            </motion.div>

            {/* Scroll indicator */}
            <motion.div
              className="absolute bottom-8 left-1/2 -translate-x-1/2"
              animate={{ y: [0, 10, 0] }}
              transition={{ repeat: Infinity, duration: 2 }}
            >
              <svg className="w-6 h-6 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </motion.div>
          </div>
        </section>

        {/* Countdown Section - Optimisé */}
        <motion.section 
          className="py-20 px-4 relative"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-900/10 to-transparent pointer-events-none"></div>
          <div className="container mx-auto relative z-10">
            <motion.h2
              className="text-4xl md:text-5xl font-bold text-center text-white mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
               Le Compte à Rebours
            </motion.h2>
            <Countdown targetDate={concertDate} language={language} />
          </div>
        </motion.section>

        {/* Event Info Section - Optimisé */}
        <motion.section 
          className="py-20 px-4"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
        >
          <div className="container mx-auto">
            <EventInfo language={language} />
          </div>
        </motion.section>

        {/* Photo Gallery Section - Optimisé */}
        <motion.section 
          className="py-20 px-4"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
        >
          <div className="container mx-auto">
            <PhotoGallery language={language} />
          </div>
        </motion.section>

        {/* Official Poster Section - Optimisé */}
        <motion.section 
          className="py-20 px-4 bg-gradient-to-b from-transparent via-navy-dark/30 to-transparent"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
        >
          <div className="container mx-auto">
            <OfficialPoster language={language} />
          </div>
        </motion.section>

        {/* Footer Amélioré */}
        <footer className="py-12 px-4 bg-navy-dark border-t border-white/10">
          <div className="container mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              <div className="text-white/60 text-center md:text-left">
                {text.footer}
              </div>
              <div className="flex gap-6">
                <a 
                  href="https://www.ticketmaster.be/event/289249177" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-white/60 hover:text-white transition-colors no-underline"
                  style={{ textDecoration: 'none' }}
                >
                  🎫 Billetterie
                </a>
                <button className="text-white/60 hover:text-white transition-colors">
                  ✉️ Contact
                </button>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default App;
